/** @format */

import React, { useEffect, useRef, useState } from "react";

import portals from "../../data/portals.json";
import { awsUrl, triggerGAEvent } from "../../helpers";

import { gsap } from "../../gsap";
import { SplitText } from "../../gsap/SplitText";
import { useGSAP } from "../../gsap/react";

import island from "../../assets/bg-island-0229.jpg";
import Modal from "../../common/Modal";
import Video from "../../common/Video";
import Portal from "./Portal";
import DetailView from "./Detail";
import CountdownClock from "../CountdownClock";
import { useEscape } from "../../hooks/useEscape";

import { useUserContext } from "../../context/UserContext";

gsap.registerPlugin(SplitText, useGSAP);

const Portals = () => {
  const mobileTl = useRef();
  const tl = useRef();
  const shineTl = useRef();
  const pinDroppedTl = useRef();
  const portalsRef = useRef();

  const mapRef = useRef();
  const [isOpen, setIsOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoPlayed, setVideoPlayed] = useState(false);
  const [selected, setSelected] = useState();
  const [portalCurrent, setPortalCurrent] = useState(null);
  const [portalPrev, setPortalPrev] = useState(null);
  const [portalNext, setPortalNext] = useState(null);
  const [pinDropped, setPinDropped] = useState(false);

  const { fromPortal } = useUserContext();

  let isMobile =
    window.matchMedia("(max-width: 992px)").matches ||
    window.matchMedia("(pointer: coarse)").matches;

  const action = isMobile ? "Tap" : "Click";

  const mountedStyle = {
    animation: "inAnimation 300ms ease-in"
  };

  const unmountedStyle = {
    animation: "outAnimation 300ms ease-out"
  };

  const filteredPortals = portals.filter((portal) => {
    return new Date(portal.release_timestamp) > Date.now();
  });

  const openModal = (portalKey, idx, nav) => {
    let selectedKeyhole = portals.filter((el) => {
      return el.key === portalKey;
    });

    const filteredIdx = filteredPortals.findIndex((portal) => {
      return portal.key === portalKey;
    });

    if (filteredPortals.length < 4) {
      setPortalCurrent(filteredIdx);
      filteredIdx === 0 ? setPortalPrev(null) : setPortalPrev(filteredPortals[filteredIdx - 1]);
      filteredIdx === 1 ? setPortalNext(null) : setPortalNext(filteredPortals[filteredIdx + 1]);
    } else if (filteredPortals.length < 5) {
      setPortalCurrent(filteredIdx);
      filteredIdx === 0 ? setPortalPrev(null) : setPortalPrev(filteredPortals[filteredIdx - 1]);
      filteredIdx === 2 ? setPortalNext(null) : setPortalNext(filteredPortals[filteredIdx + 1]);
    } else {
      setPortalCurrent(idx);
      idx === 0 ? setPortalPrev(null) : setPortalPrev(portals[idx - 1]);
      idx === 4 ? setPortalNext(null) : setPortalNext(portals[idx + 1]);
    }

    setSelected(selectedKeyhole);
    setIsOpen(true);

    nav === "pin"
      ? triggerGAEvent("home_map_keyhole_" + portalKey)
      : triggerGAEvent("portal_" + portalKey + "_next-portal");
  };

  const closeModal = () => {
    setIsOpen(false);
    triggerGAEvent("home_portal_close");
  };

  useEscape(closeModal);

  const pinDrop = () => {
    setPinDropped(true);
  };

  const dropPins = () => {
    setIsPlaying(true);

    setTimeout(() => {
      setVideoPlayed(true);

      pinDroppedTl.current.play();
    }, 4000);
  };

  useEffect(() => {
    if (fromPortal) {
      isMobile ? mobileTl.current.progress(1) : tl.current.progress(1);
      setVideoPlayed(true);
      pinDroppedTl.current.progress(1);
    }
  }, [fromPortal, isMobile]);

  const portalsSorted = [...portals].sort((a, b) => a.pinOrder - b.pinOrder);
  const keyholes = portalsSorted.map((portal) => {
    const idx = portals.findIndex((el) => el.key === portal.key);
    return (
      <Portal
        key={idx}
        previewPortal={() => openModal(portal.key, idx, "pin")}
        portal={portal}
        pinDrop={pinDropped}
      />
    );
  });

  const nextPortal = portals
    .filter((portal, idx) => {
      return new Date(portal.release_timestamp) > Date.now() && portal.key !== "welcome";
    })
    .map((portal) => {
      return [{ name: portal.name, timestamp: portal.release_timestamp }];
    })
    .sort((a, b) => a.release_timestamp - b.release_timestamp);

  useGSAP(() => {
    let titleRef = new SplitText("#titleRef", { type: "chars, words" });
    let mm = gsap.matchMedia();

    mm.add("(max-width: 992px)", () => {
      mobileTl.current = gsap
        .timeline({
          scrollTrigger: {
            start: "top 80%",
            trigger: portalsRef.current
          }
        })
        .from(
          titleRef.chars,
          {
            duration: 0.65,
            x: 10,
            autoAlpha: 0,
            stagger: 0.03,
            onComplete: dropPins
          },
          "<"
        )
        .fromTo("#portals-header", { opacity: 0, y: 10 }, { opacity: 1, y: 0 }, ">");
    });

    mm.add("(min-width: 993px)", () => {
      tl.current = gsap
        .timeline({
          scrollTrigger: {
            start: "top center",
            trigger: mapRef.current
            // markers: true,
          }
        })
        .from(
          titleRef.chars,
          {
            duration: 0.65,
            x: 10,
            autoAlpha: 0,
            stagger: 0.03,
            onComplete: dropPins
          },
          "<"
        );

      shineTl.current = gsap
        .timeline({ repeat: -1 })
        .to(".shine-top", { opacity: 1, x: "700px", duration: 0.45, delay: 5 }, ">-=1");
    });

    pinDroppedTl.current = gsap
      .timeline({ paused: true })
      .fromTo("#portals-header", { opacity: 0, y: 10 }, { opacity: 1, y: 0 }, ">")
      .from(
        "#portalsCaptionRef",
        {
          y: 10,
          autoAlpha: 0,
          onComplete: pinDrop
        },
        ">"
      );
  }, {});

  return (
    <section
      ref={portalsRef}
      id="section-portals"
      className="md:h-screen w-full flex flex-col bg-dark-blue bg-gradient-top pointer-events-none overflow-hidden"
      data-tag="section_3_portal"
      data-pixel="carni041+unique"
    >
      <h2
        aria-label="Discover your paradise at Celebration Key™"
        id="titleRef"
        className="mt-[42px] text-[30px] leading-[41px] lg:text-[45px] text-white relative z-10 w-full"
      >
        <span aria-hidden="true">
          Discover your paradise at Celebration Key
          <sup className="text-[10px] -top-[10px] ml-[1.5px] lg:ml-[3px] lg:text-[16px] lg:-top-[15px]">
            &trade;
          </sup>
        </span>
      </h2>

      <p
        id="portals-header"
        className="px-6 z-10 text-white lg:text-[18px] w-full max-w-[745px] mx-auto lg:leading-[24px] portal--copy my-4 pointer-events-none "
      >
        No matter what paradise means to you – be it relaxing beneath swaying palms, making new
        family memories, or diving into a freshwater lagoon – you’ll find it in the unforgettable
        Portals at Carnival’s Celebration&nbsp;Key™.
      </p>
      <p
        id="portalsCaptionRef"
        className="lg:text-[18px] drop-shadow-2xl text-white z-50 pointer-events-none font-semibold"
      >
        {action} a map pin to explore{" "}
        <img
          aria-hidden="true"
          alt="down arrow"
          className="inline w-4 ml-3"
          src={awsUrl + `/media/down-arrow-white.svg`}
        />
      </p>

      <div
        className={`portals-container mt-20 lg:mt-0 lg:absolute lg:left-1/2 lg:-translate-x-1/2 w-full h-screen h-full lg:h-full lg:overflow-hidden `}
      >
        <div className="map-container">
          {" "}
          <div
            id="mapRef"
            ref={mapRef}
            className={
              !videoPlayed
                ? `inline-block relative w-full pointer-events-auto overflow-hidden h-screen`
                : `inline-block relative w-full pointer-events-auto overflow-hidden`
            }
          >
            {!videoPlayed ? (
              <div style={{ mountedStyle }} className={`absolute z-[10] w-full fadeOut`}>
                <Video
                  poster={"portals-flythrough"}
                  name={"portals/flythrough"}
                  autoplay={isPlaying}
                ></Video>
              </div>
            ) : (
              <img
                id="imageMap"
                style={{ unmountedStyle }}
                className={
                  `block bg-portals z-0 relative pointer-events-auto ` && !videoPlayed
                    ? `inAnimation`
                    : `outAnimation`
                }
                src={island}
                alt="island"
                aria-hidden="true"
              />
            )}

            {keyholes}
          </div>
        </div>
      </div>
      {!isMobile ? (
        <div className="ribbon bg-dark-blue">
          <div>
            <div className="ml-auto lg:w-8/12 bg-dark-blue h-full flex items-center justify-center lg:justify-end pt-5 lg:pt-0">
              <p className="lg:mr-0 max-w-[475px] mx-auto lg:m-0 text-[18px] leading-[24px] lg:pr-20 mb-9 lg:mb-0 lg:text-left px-4 lg:px-0 mt-2 lg:mt-0">
                New updates are coming soon! Check in as we unlock even more Portals and count down
                the days until Celebration Key™ opens.
              </p>
            </div>
            <div className="text-center text-3xl bg-[#0091D0] h-full mx-auto flex items-center font-semibold w-full lg:w-auto lg:pl-[60px] lg:pr-[80px] countdown-container justify-around lg:mt-0">
              <div className="overflow-hidden lg:overflow-auto">
                <div className="max-w-[300px] w-[300px] mx-auto lg:ml-0 ">
                  <div className="shine-top"></div>
                  <span className="text-[15px] uppercase block font-bold text-white z-10 relative">
                    {nextPortal[0][0].name}&nbsp;Portal&nbsp;unlocks&nbsp;in
                  </span>
                  <CountdownClock date={nextPortal[0][0].timestamp} />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="ribbon bg-dark-blue">
          <div>
            <div className="px-3 pt-5">
              <p className="mb-9">
                New updates are coming soon! Check in as we unlock even more Portals and count down
                the days until Celebration Key™ opens.
              </p>
            </div>
            <div className="text-center text-3xl bg-[#0091D0] h-full mx-auto flex items-center font-semibold w-full lg:w-auto lg:pl-[60px] lg:pr-[80px] countdown-container justify-around lg:mt-0">
              <div className="overflow-hidden lg:overflow-auto">
                <div className="max-w-[300px] w-[300px] mx-auto lg:ml-0 ">
                  <div className="shine-top"></div>
                  <span className="text-[15px] uppercase block font-bold text-white z-10 relative">
                    {nextPortal[0][0].name}&nbsp;Portal&nbsp;unlocks&nbsp;in
                  </span>
                  <CountdownClock date={nextPortal[0][0].timestamp} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isOpen && (
        <Modal show={isOpen} handleClose={closeModal}>
          <div className="w-full absolute left-0 bottom-20 z-10 text-[#7AC7FF] pointer-events-auto">
            {portalPrev !== null && (
              <div
                tabIndex={0}
                aria-label={portalPrev.name + ` portal`}
                onClick={() => {
                  openModal(portalPrev.key, portalCurrent - 1, "nav");
                }}
                onKeyDown={(e) => {
                  openModal(portalPrev.key, portalCurrent - 1, "nav", e);
                }}
                className=" absolute left-5 hover:opacity-75 cursor-pointer uppercase flex items-center text-left"
              >
                <img
                  src={awsUrl + `/media/double-arrow.svg`}
                  alt="left arrow"
                  className="w-[24px] mr-3"
                />
                <span className="leading-tight">
                  {portalPrev.name} <span className="block">portal</span>
                </span>
              </div>
            )}

            {portalNext !== null && (
              <div
                tabIndex={0}
                aria-label={portalNext.name + ` portal`}
                onClick={() => {
                  openModal(portalNext.key, portalCurrent + 1, "nav");
                }}
                onKeyDown={(e) => {
                  openModal(portalNext.key, portalCurrent + 1, "nav", e);
                }}
                className=" absolute right-5  hover:opacity-75 cursor-pointer uppercase flex items-center text-right pointer-events-auto"
              >
                <span className="leading-tight">
                  {portalNext.name} <span className="block">portal</span>
                </span>

                <img
                  src={awsUrl + `/media/double-arrow.svg`}
                  alt="right arrow"
                  className="w-[24px] ml-3 rotate-180"
                />
              </div>
            )}
          </div>
          <DetailView
            key={selected[0].key}
            delay={selected[0].delayTime}
            portalKey={selected[0].key}
          />
        </Modal>
      )}
    </section>
  );
};
export default Portals;
